import { useMemo } from 'react';
import { useTheme } from 'next-themes';

/** Hex tokens for Plotly — CSS vars are unreliable in SVG text fills. Match index.css. */
export type PlotlyThemeColors = {
  text: string;
  mutedText: string;
  grid: string;
  legendBg: string;
  legendBorder: string;
  hoverBg: string;
  recessionFill: string;
  recessionBorder: string;
  recessionLabel: string;
  recessionLabelBg: string;
};

export function getPlotlyThemeColors(isDark: boolean): PlotlyThemeColors {
  return isDark
    ? {
        text: '#d5dbe3',
        mutedText: '#9ca3af',
        grid: 'rgba(213, 219, 227, 0.12)',
        legendBg: 'rgba(36, 31, 30, 0.92)',
        legendBorder: 'rgba(213, 219, 227, 0.18)',
        hoverBg: '#2b2624',
        recessionFill: 'rgba(239, 68, 68, 0.22)',
        recessionBorder: '#f87171',
        recessionLabel: '#fca5a5',
        recessionLabelBg: 'rgba(127, 29, 29, 0.55)',
      }
    : {
        text: '#1e2430',
        mutedText: '#64748b',
        grid: 'rgba(0, 0, 0, 0.1)',
        legendBg: 'rgba(255, 255, 255, 0.92)',
        legendBorder: 'rgba(0, 0, 0, 0.12)',
        hoverBg: '#ffffff',
        recessionFill: 'rgba(239, 68, 68, 0.15)',
        recessionBorder: '#ef4444',
        recessionLabel: '#ef4444',
        recessionLabelBg: 'rgba(239, 68, 68, 0.1)',
      };
}

export function usePlotlyTheme() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const colors = useMemo(() => getPlotlyThemeColors(isDark), [isDark]);
  return { isDark, colors, themeKey: resolvedTheme ?? 'light' };
}

/** Shared axis / title / legend / hover pieces for transparent-paper charts. */
export function plotlyChartChrome(
  colors: PlotlyThemeColors,
  options: { isMobile?: boolean; fontSize?: number } = {}
) {
  const { isMobile = false, fontSize = isMobile ? 11 : 12 } = options;

  const axisTitle = (text: string | undefined) =>
    text
      ? { text, font: { size: fontSize, color: colors.mutedText } }
      : undefined;

  return {
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    font: {
      family: 'Inter, sans-serif',
      size: fontSize,
      color: colors.text,
    },
    hoverlabel: {
      bgcolor: colors.hoverBg,
      bordercolor: colors.legendBorder,
      font: { color: colors.text, family: 'Inter, sans-serif', size: fontSize },
    },
    titleFont: { size: isMobile ? 13 : 16, color: colors.text },
    axisTitle,
    axisDefaults: {
      color: colors.text,
      gridcolor: colors.grid,
      zerolinecolor: colors.grid,
      linecolor: colors.grid,
      tickfont: { size: fontSize, color: colors.text },
      titlefont: { size: fontSize, color: colors.mutedText },
    },
    legend: (placement: 'overlay' | 'bottom' = isMobile ? 'bottom' : 'overlay') =>
      placement === 'bottom'
        ? {
            orientation: 'h' as const,
            x: 0,
            y: -0.28,
            bgcolor: colors.legendBg,
            bordercolor: colors.legendBorder,
            borderwidth: 1,
            font: { size: 11, color: colors.text },
          }
        : {
            x: 0.02,
            y: 0.98,
            bgcolor: colors.legendBg,
            bordercolor: colors.legendBorder,
            borderwidth: 1,
            font: { color: colors.text },
          },
  };
}

/** Merge consecutive recession years so one label covers each band (avoids overlap). */
export function clusterRecessionYears(years: number[]): Array<{ start: number; end: number; mid: number }> {
  const sorted = [...new Set(years)].sort((a, b) => a - b);
  if (sorted.length === 0) return [];

  const clusters: Array<{ start: number; end: number; mid: number }> = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] <= end + 1) {
      end = sorted[i];
    } else {
      clusters.push({ start, end, mid: (start + end) / 2 });
      start = sorted[i];
      end = sorted[i];
    }
  }
  clusters.push({ start, end, mid: (start + end) / 2 });
  return clusters;
}

export function recessionAnnotations(
  years: number[],
  colors: PlotlyThemeColors,
  options: { enabled?: boolean } = {}
) {
  if (options.enabled === false) return [];

  return clusterRecessionYears(years).map(({ start, end, mid }) => ({
    x: mid,
    y: 0.96,
    yref: 'y3' as const,
    text: start === end ? 'Recession' : `${start}–${String(end).slice(-2)}`,
    showarrow: false,
    font: { size: 9, color: colors.recessionLabel },
    bgcolor: colors.recessionLabelBg,
    bordercolor: colors.recessionBorder,
    borderwidth: 1,
    xanchor: 'center' as const,
    yanchor: 'top' as const,
  }));
}
